import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Checkbox,
  HStack,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  Tooltip,
  useToast,
} from "@chakra-ui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload } from "@fortawesome/free-solid-svg-icons";
import moment from "moment";
import { cqAPI } from "../../lib/Auth";

const REPORT_DOWNLOADS = {
  "/reports/calls": { endpoint: "/reports/calls", fileName: "call-list" },
  "/reports/audit": { endpoint: "/audit", fileName: "audit-trail" },
  "/reports/campaigns": { endpoint: "/reports/campaigns", fileName: "campaigns" },
  "/reports/ai_actions": { endpoint: "/reports/ai-actions", fileName: "ai-actions" },
  "/reports/ai_revenue": { endpoint: "/reports/ai-revenue", fileName: "ai-revenue" },
  "/reports/forms": { endpoint: "/reports/forms", fileName: "forms-and-history" },
  "/reports/leads": { endpoint: "/reports/leads", fileName: "lead-conversion" },
  "/reports/unscheduled": { endpoint: "/reports/unscheduled", fileName: "unscheduled-patients" },
  "/reports/huddle": { endpoint: "/reports/huddle", fileName: "daily-huddle" },
  "/reports/lead-ads": { endpoint: "/reports/lead-ads", fileName: "lead-ads" },
  "/reports/payments": { endpoint: "/reports/payments", fileName: "patient-payments" },
  "/reports/payment-requests": { endpoint: "/reports/payrequests", fileName: "payment-requests" },
  "/reports/installments": { endpoint: "/reports/installments", fileName: "installment-plans" },
  "/reports/discount": { endpoint: "/reports/discount", fileName: "discount-plans" },
};

const setDateParams = (params, hashParams, reportPath) => {
  const startDate = hashParams.get("startDate");
  const endDate = hashParams.get("endDate");

  if (!startDate || !endDate) {
    return;
  }

  if (reportPath === "/reports/lead-ads") {
    params.set("start", moment(startDate).startOf("day").toISOString());
    params.set("end", moment(endDate).endOf("day").toISOString());
    return;
  }

  params.set("rangeBeg", moment(startDate).format("L"));
  params.set("rangeEnd", moment(endDate).format("L"));
};

const buildDownloadEndpoint = (reportPath) => {
  const report = REPORT_DOWNLOADS[reportPath];
  const params = new URLSearchParams(window.location.search);
  const hashParams = new URLSearchParams(window.location.hash.slice(1));

  params.set("format", "csv");
  setDateParams(params, hashParams, reportPath);

  if (hashParams.get("campaign")) params.set("campaign", hashParams.get("campaign"));
  if (hashParams.get("campaign_id")) params.set("campaign_id", hashParams.get("campaign_id"));
  if (hashParams.get("phoneNumber")) params.set("phone_number", hashParams.get("phoneNumber"));
  if (hashParams.get("conversationTopics")) params.set("conversation_topics", hashParams.get("conversationTopics"));
  if (hashParams.get("patientType")) params.set("patient_type", hashParams.get("patientType"));
  if (hashParams.get("period")) params.set("period", hashParams.get("period"));

  const queryString = params.toString();
  return `${report.endpoint}${queryString ? `?${queryString}` : ""}`;
};

const hasRequiredParams = (reportPath) => {
  if (reportPath !== "/reports/campaigns") {
    return true;
  }

  return !!new URLSearchParams(window.location.hash.slice(1)).get("campaign_id");
};

const isVisible = (element) => {
  if (!element) {
    return false;
  }

  const style = window.getComputedStyle(element);
  return style.display !== "none" && style.visibility !== "hidden" && element.getClientRects().length > 0;
};

const elementText = (element) => (element?.innerText || element?.textContent || "").replace(/\s+/g, " ").trim();

const toCsvCell = (value) => {
  const text = value === null || value === undefined ? "" : String(value);
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

const rowsToCsv = (rows) => rows.map((row) => row.map(toCsvCell).join(",")).join("\r\n");

const getContentNodes = (targetElement) => {
  const nodes = [];
  let node = targetElement?.nextElementSibling;

  while (node) {
    if (isVisible(node)) {
      nodes.push(node);
    }
    node = node.nextElementSibling;
  }

  return nodes;
};

const extractTables = (nodes) => {
  const rows = [];
  nodes.forEach((node) => {
    const tables = [
      ...(node.matches("table") ? [node] : []),
      ...Array.from(node.querySelectorAll("table")),
    ];

    tables.forEach((table) => {
      if (!isVisible(table)) {
        return;
      }
      if (rows.length) {
        rows.push([]);
      }
      let columnCount = 0;
      Array.from(table.querySelectorAll("tr")).forEach((tr) => {
        if (!isVisible(tr)) {
          return;
        }
        const cellElements = Array.from(tr.children).filter((child) => child.matches("th,td"));
        const rowColumnCount = cellElements.reduce((count, cell) => count + cell.colSpan, 0);
        if (cellElements.length === 1 && rowColumnCount > 1) {
          return;
        }
        if (!columnCount && rowColumnCount > 1) {
          columnCount = rowColumnCount;
        }
        if (columnCount && rowColumnCount !== columnCount) {
          return;
        }
        const cells = cellElements.map(elementText);
        if (cells.some(Boolean)) {
          rows.push(cells);
        }
      });
    });
  });

  return rows;
};

const ignoredStructuredElement = (element) => element.matches("button,select,input,textarea,svg,canvas,table");

const visibleChildren = (element) => Array.from(element.children).filter(isVisible);

const rowCandidateChildren = (element) => {
  let children = visibleChildren(element).filter((child) => !ignoredStructuredElement(child));

  if (children.length === 1 && visibleChildren(children[0]).length > 1) {
    children = visibleChildren(children[0]).filter((child) => !ignoredStructuredElement(child));
  }

  return children;
};

const rowCells = (element) => {
  const children = rowCandidateChildren(element);

  if (children.length < 2 || children.length > 12) {
    return null;
  }

  const cells = children.map(elementText);
  return cells.filter(Boolean).length >= 2 ? cells : null;
};

const hasRowLikeChildren = (element) => {
  const children = rowCandidateChildren(element);
  if (children.length < 2) {
    return false;
  }

  return children.filter((child) => rowCells(child)).length >= 2;
};

const extractStructuredRows = (nodes) => {
  const rows = [];
  const seen = new Set();

  const visit = (element) => {
    if (!isVisible(element) || ignoredStructuredElement(element)) {
      return;
    }

    if (hasRowLikeChildren(element)) {
      rowCandidateChildren(element).forEach(visit);
      return;
    }

    const cells = rowCells(element);
    const key = cells?.join("\u001f");
    if (cells && !seen.has(key)) {
      rows.push(cells);
      seen.add(key);
      return;
    }

    Array.from(element.children).forEach(visit);
  };

  nodes.forEach(visit);
  return rows;
};

const visibleReportCsv = (targetElement) => {
  const nodes = getContentNodes(targetElement);
  if (!nodes.length) {
    return "";
  }

  const tableRows = extractTables(nodes);
  if (tableRows.length) {
    return rowsToCsv(tableRows);
  }

  const structuredRows = extractStructuredRows(nodes);
  if (structuredRows.length) {
    return rowsToCsv(structuredRows);
  }

  const textRows = nodes.map(elementText).filter(Boolean).map((text) => [text]);
  return textRows.length ? rowsToCsv(textRows) : "";
};

const downloadText = (content, fileName) => {
  const blobUrl = window.URL.createObjectURL(new Blob([`\ufeff${content}`], { type: "text/csv;charset=utf-8" }));
  const link = document.createElement("a");
  link.href = blobUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(blobUrl);
};

function ReportDownloadButton({ reportPath, isDisabled, targetElement }) {
  const [isDownloading, setIsDownloading] = useState(false);
  const toast = useToast();
  const report = REPORT_DOWNLOADS[reportPath];

  if (!report) {
    return null;
  }

  const downloadReport = async () => {
    setIsDownloading(true);
    try {
      const visibleCsv = visibleReportCsv(targetElement);
      if (visibleCsv) {
        downloadText(visibleCsv, `${report.fileName}.csv`);
        return;
      }

      const blobUrl = await cqAPI.downloadFile(buildDownloadEndpoint(reportPath));
      if (!blobUrl) {
        throw new Error("Download failed");
      }

      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = `${report.fileName}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      toast({
        title: "Could not download report",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsDownloading(false);
    }
  };

  const disabled = isDisabled || isDownloading;

  return (
    <Tooltip label={disabled ? "Available after the report is visible" : "Download this report as CSV"}>
      <span>
        <Button
          size="sm"
          variant="outline"
          leftIcon={<FontAwesomeIcon icon={faDownload} />}
          onClick={downloadReport}
          isLoading={isDownloading}
          isDisabled={disabled}
        >
          Download CSV
        </Button>
      </span>
    </Tooltip>
  );
}

export default function ReportDownloadWrapper({ reportPath, children }) {
  const contentRef = useRef(null);
  const [target, setTarget] = useState(null);
  const [isReportVisible, setIsReportVisible] = useState(false);
  const [locations, setLocations] = useState([]);
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const hasSelectedBusiness = !!queryParams.get("bid");
  const selectedBids = (queryParams.get("bids") || "").split(",").filter(Boolean);
  const showLocationSelector = REPORT_DOWNLOADS[reportPath] && !hasSelectedBusiness;

  useEffect(() => {
    if (!showLocationSelector) {
      setLocations([]);
      return undefined;
    }

    let isMounted = true;
    cqAPI.get(`/reports/locations?path=${encodeURIComponent(reportPath)}`).then((response) => {
      if (isMounted && response?.type === "ok") {
        setLocations(response.data || []);
      }
    });

    return () => {
      isMounted = false;
    };
  }, [reportPath, showLocationSelector]);

  useEffect(() => {
    if (!showLocationSelector || !locations.length || selectedBids.length) {
      return;
    }

    const nextParams = new URLSearchParams(location.search);
    nextParams.set("bids", locations.map((loc) => loc.bid).join(","));
    navigate(`${location.pathname}?${nextParams.toString()}`, { replace: true });
  }, [location.pathname, location.search, locations, navigate, selectedBids.length, showLocationSelector]);

  const activeBids = selectedBids.length ? selectedBids : locations.map((loc) => loc.bid);
  const setActiveBids = (nextBids) => {
    if (!nextBids.length) {
      return;
    }
    const nextParams = new URLSearchParams(location.search);
    nextParams.set("bids", nextBids.join(","));
    navigate(`${location.pathname}?${nextParams.toString()}`);
  };

  const toggleLocation = (bid) => {
    const nextBids = activeBids.includes(bid)
      ? activeBids.filter((selectedBid) => selectedBid !== bid)
      : [...activeBids, bid];
    setActiveBids(nextBids);
  };

  const locationButtonLabel = activeBids.length === locations.length
    ? "All locations"
    : `${activeBids.length} location${activeBids.length === 1 ? "" : "s"}`;

  useEffect(() => {
    if (!REPORT_DOWNLOADS[reportPath] || !contentRef.current) {
      return undefined;
    }

    const updateVisibility = () => {
      const content = contentRef.current;
      const nextTarget = content.querySelector("[data-report-download-target]");
      const hasSpinner = !!content.querySelector(".chakra-spinner");
      setTarget(nextTarget);
      setIsReportVisible(!!nextTarget && !hasSpinner && hasRequiredParams(reportPath));
    };

    updateVisibility();
    const observer = new MutationObserver(updateVisibility);
    observer.observe(contentRef.current, {
      attributes: true,
      childList: true,
      subtree: true,
    });
    window.addEventListener("hashchange", updateVisibility);

    return () => {
      observer.disconnect();
      window.removeEventListener("hashchange", updateVisibility);
    };
  }, [reportPath]);

  if (!REPORT_DOWNLOADS[reportPath]) {
    return children;
  }

  return (
    <Box w="100%">
      {showLocationSelector && locations.length > 1 && (
        <HStack justify="flex-end" mb={3} className="no-print">
          <Text fontSize="sm" color="gray.500">Locations</Text>
          <Menu closeOnSelect={false}>
            <MenuButton as={Button} size="sm" variant="outline">
              {locationButtonLabel}
            </MenuButton>
            <MenuList zIndex="popover">
              {locations.map((loc) => (
                <MenuItem key={loc.bid} onClick={() => toggleLocation(loc.bid)}>
                  <Checkbox isChecked={activeBids.includes(loc.bid)} pointerEvents="none" mr={2}>
                    {loc.name}
                  </Checkbox>
                </MenuItem>
              ))}
            </MenuList>
          </Menu>
        </HStack>
      )}
      <Box ref={contentRef}>
        {children}
      </Box>
      {target && createPortal(
        <ReportDownloadButton reportPath={reportPath} isDisabled={!isReportVisible} targetElement={target} />,
        target
      )}
    </Box>
  );
}
